<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">Generate Agent Report</h3>
        <button @click="closeModal" class="modal-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div v-if="agent" class="agent-info">
          <div class="agent-details">
            <img :src="agent.emoji_url" :alt="agent.name" class="agent-avatar" />
            <div class="agent-text">
              <h4 class="agent-name">{{ agent.name }}</h4>
              <p class="agent-code">{{ agent.agent_code }}</p>
              <p class="agent-department">{{ agent.department_name }}</p>
            </div>
          </div>
        </div>

        <div class="date-selection">
          <div class="form-group">
            <label for="startDate" class="form-label">Start Date</label>
            <input
              id="startDate"
              v-model="formData.startDate"
              type="date"
              class="form-input"
              required
              :max="formData.endDate"
            />
          </div>

          <div class="form-group">
            <label for="endDate" class="form-label">End Date</label>
            <input
              id="endDate"
              v-model="formData.endDate"
              type="date"
              class="form-input"
              required
              :min="formData.startDate"
              :max="maxDate"
            />
          </div>
        </div>

        <div class="preset-buttons">
          <button type="button" @click="setPreset('7days')" class="preset-btn">Last 7 Days</button>
          <button type="button" @click="setPreset('30days')" class="preset-btn">Last 30 Days</button>
          <button type="button" @click="setPreset('90days')" class="preset-btn">Last 90 Days</button>
        </div>

        <div class="form-actions">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading || !formData.startDate || !formData.endDate"
          >
            <svg v-if="loading" class="btn-spinner" width="16" height="16" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            {{ loading ? 'Generating...' : 'Generate Report' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportGenerationModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    agent: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      formData: {
        startDate: '',
        endDate: ''
      },
      loading: false
    }
  },
  computed: {
    maxDate () {
      // Get current date and time
      const now = new Date()
      // Add one day to allow selecting today even after midnight
      now.setDate(now.getDate() + 1)
      return now.toISOString().split('T')[0]
    }
  },
  watch: {
    visible (newVal) {
      if (newVal && this.agent) {
        this.setPreset('30days')
      }
    }
  },
  methods: {
    setPreset (preset) {
      const endDate = new Date()
      const startDate = new Date()

      switch (preset) {
        case '7days':
          startDate.setDate(endDate.getDate() - 7)
          break
        case '30days':
          startDate.setDate(endDate.getDate() - 30)
          break
        case '90days':
          startDate.setDate(endDate.getDate() - 90)
          break
      }

      this.formData.startDate = startDate.toISOString().split('T')[0]
      this.formData.endDate = endDate.toISOString().split('T')[0]
    },

    async handleSubmit () {
      if (!this.agent || !this.formData.startDate || !this.formData.endDate) {
        return
      }

      this.loading = true
      try {
        const result = {
          agentId: this.agent.id,
          startDate: this.formData.startDate,
          endDate: this.formData.endDate
        }

        this.$emit('generate', result)
      } catch (error) {
        console.error('Error preparing report data:', error)
      } finally {
        this.loading = false
      }
    },

    closeModal () {
      this.formData.startDate = ''
      this.formData.endDate = ''
      this.loading = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
  border-bottom: 1px solid var(--border-primary);
  margin-bottom: 24px;
  padding-bottom: 16px;
  transition: border-color 0.3s ease;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 0 24px 24px;
}

.agent-info {
  margin-bottom: 24px;
}

.agent-details {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.agent-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  padding: 4px;
  transition: background-color 0.3s ease;
}

.agent-text {
  flex: 1;
}

.agent-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
  transition: color 0.3s ease;
}

.agent-code {
  font-size: 12px;
  color: var(--text-secondary);
  font-family: monospace;
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  margin: 0 0 4px;
  transition: color 0.3s ease, background-color 0.3s ease;
}

.agent-department {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

.date-selection {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 6px;
  transition: color 0.3s ease;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.preset-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 8px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.preset-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.preset-btn:active {
  background: var(--bg-tertiary);
  transform: translateY(1px);
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  justify-content: center;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header,
  .modal-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .date-selection {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .preset-buttons {
    justify-content: center;
  }
}
</style>
